import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useIsMobile } from '../../hooks/useIsMobile';
import { listInstallers } from './api';
const PAGE_LIMIT = 20;
export const InstallerList = () => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [nameSearch, setNameSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [tokenStack, setTokenStack] = useState([undefined]);
    const [pageIndex, setPageIndex] = useState(0);
    const currentToken = tokenStack[pageIndex];
    const debounceRef = useRef(null);
    const load = useCallback(async (name) => {
        setLoading(true);
        setError(null);
        try {
            const data = await listInstallers({
                name: (name ?? nameSearch) || undefined,
                status: statusFilter || undefined,
                limit: PAGE_LIMIT,
                nextToken: currentToken,
            });
            setResult(data);
        }
        catch (e) {
            setError(e.message ?? 'Failed to load installers');
        }
        finally {
            setLoading(false);
        }
    }, [nameSearch, statusFilter, currentToken]);
    useEffect(() => {
        load();
    }, [load]);
    const resetPagination = () => {
        setTokenStack([undefined]);
        setPageIndex(0);
    };
    const handleNameChange = (e) => {
        const val = e.target.value;
        setNameSearch(val);
        resetPagination();
        if (debounceRef.current)
            clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            load(val);
        }, 300);
    };
    const handleNameKeyDown = (e) => {
        if (e.key === 'Enter') {
            if (debounceRef.current)
                clearTimeout(debounceRef.current);
            resetPagination();
            load(nameSearch);
        }
    };
    const handleStatusFilter = (val) => {
        setStatusFilter(val);
        resetPagination();
    };
    const handleNext = () => {
        if (!result?.nextToken)
            return;
        const next = pageIndex + 1;
        const newStack = tokenStack.slice(0, next);
        newStack.push(result.nextToken);
        setTokenStack(newStack);
        setPageIndex(next);
    };
    const handlePrev = () => {
        if (pageIndex === 0)
            return;
        setPageIndex(pageIndex - 1);
    };
    return (_jsx(Layout, { children: _jsxs("div", { children: [_jsxs("div", { style: styles.pageHeader, children: [_jsxs("div", { children: [_jsx("h2", { style: styles.heading, children: "Team" }), _jsx("p", { style: styles.subheading, children: "Manage field team members" })] }), _jsx("button", { style: styles.primaryButton, onClick: () => navigate('/installers/new'), children: "+ New Installer" })] }), _jsxs("div", { style: {
                        ...styles.filtersCard,
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'stretch' : 'center',
                    }, children: [_jsx("input", { style: { ...styles.searchInput, minWidth: isMobile ? '0' : '200px' }, type: "text", placeholder: "Search by name\u2026", value: nameSearch, onChange: handleNameChange, onKeyDown: handleNameKeyDown }), _jsx("div", { style: {
                                ...styles.statusFilters,
                                flexDirection: isMobile ? 'column' : 'row',
                            }, children: ['', 'active', 'inactive'].map((val) => (_jsx("button", { style: {
                                    ...styles.filterBtn,
                                    ...(statusFilter === val ? styles.filterBtnActive : {}),
                                    minHeight: isMobile ? '44px' : undefined,
                                }, onClick: () => handleStatusFilter(val), children: val === '' ? 'All' : val === 'active' ? 'Active' : 'Inactive' }, val))) })] }), error && _jsx("div", { style: styles.errorBanner, children: error }), loading && !result && (_jsx("div", { style: styles.loadingCenter, children: _jsx("div", { style: styles.spinner }) })), result && result.items.length === 0 && !loading && (_jsx("p", { style: styles.emptyText, children: "No installers found." })), _jsx("div", { style: {
                        ...styles.grid,
                        gridTemplateColumns: isMobile
                            ? 'minmax(100%, 1fr)'
                            : 'repeat(auto-fill, minmax(280px, 1fr))',
                        opacity: loading ? 0.6 : 1,
                    }, children: result?.items.map((installer) => (_jsx(InstallerCard, { installer: installer, onClick: () => navigate(`/installers/${installer.id}`) }, installer.id))) }), result && (result.items.length > 0 || pageIndex > 0) && (_jsxs("div", { style: styles.pagination, children: [_jsx("button", { style: styles.pageButton, onClick: handlePrev, disabled: pageIndex === 0, children: "\u2190 Previous" }), _jsxs("span", { style: styles.pageInfo, children: ["Page ", pageIndex + 1] }), _jsx("button", { style: styles.pageButton, onClick: handleNext, disabled: !result?.nextToken, children: "Next \u2192" })] }))] }) }));
};
const InstallerCard = ({ installer, onClick }) => {
    const [hovered, setHovered] = useState(false);
    return (_jsxs("div", { style: {
            ...styles.card,
            transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
            boxShadow: hovered
                ? '0 12px 40px rgba(0,0,0,0.12)'
                : '0 4px 20px rgba(0,0,0,0.07)',
            cursor: 'pointer',
        }, onClick: onClick, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), children: [_jsxs("div", { style: styles.cardHeader, children: [_jsxs("span", { style: styles.installerName, children: [installer.firstName, " ", installer.lastName] }), _jsx("span", { style: installer.status === 'active' ? styles.badgeGreen : styles.badgeGray, children: installer.status === 'active' ? 'Active' : 'Inactive' })] }), _jsx("p", { style: styles.cardDetail, children: installer.email }), installer.phone && _jsx("p", { style: styles.cardDetail, children: installer.phone }), installer.skills.length > 0 && (_jsx("div", { style: styles.skillsRow, children: installer.skills.map((skill) => (_jsx("span", { style: styles.skillChip, children: skill }, skill))) }))] }));
};
const styles = {
    pageHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: '24px',
    },
    heading: {
        fontSize: '42px',
        fontWeight: '700',
        margin: '0 0 8px 0',
        color: '#1d1d1f',
        letterSpacing: '-0.5px',
    },
    subheading: {
        fontSize: '17px',
        fontWeight: '400',
        margin: 0,
        color: '#86868b',
    },
    primaryButton: {
        padding: '12px 24px',
        backgroundColor: '#0071e3',
        color: '#fff',
        border: 'none',
        borderRadius: '980px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    filtersCard: {
        display: 'flex',
        gap: '12px',
        padding: '20px 24px',
        backgroundColor: 'rgba(255,255,255,0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '20px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
        marginBottom: '20px',
        flexWrap: 'wrap',
    },
    searchInput: {
        flex: 1,
        minWidth: '200px',
        padding: '10px 14px',
        fontSize: '15px',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: '12px',
        backgroundColor: 'rgba(255,255,255,0.7)',
        color: '#1d1d1f',
        outline: 'none',
    },
    statusFilters: {
        display: 'flex',
        gap: '8px',
    },
    filterBtn: {
        padding: '9px 18px',
        backgroundColor: 'rgba(255,255,255,0.7)',
        border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#86868b',
        cursor: 'pointer',
    },
    filterBtnActive: {
        backgroundColor: '#0071e3',
        borderColor: '#0071e3',
        color: '#fff',
        fontWeight: '600',
    },
    errorBanner: {
        padding: '14px 20px',
        backgroundColor: 'rgba(255,59,48,0.1)',
        border: '1px solid rgba(255,59,48,0.3)',
        borderRadius: '12px',
        color: '#c0392b',
        fontSize: '14px',
        marginBottom: '16px',
    },
    loadingCenter: {
        display: 'flex',
        justifyContent: 'center',
        padding: '60px 0',
    },
    spinner: {
        width: '32px',
        height: '32px',
        border: '3px solid rgba(0,0,0,0.1)',
        borderTopColor: '#0071e3',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    emptyText: {
        textAlign: 'center',
        padding: '60px 24px',
        color: '#86868b',
        fontSize: '16px',
        margin: 0,
    },
    grid: {
        display: 'grid',
        gap: '16px',
        transition: 'opacity 0.2s ease',
    },
    card: {
        padding: '24px',
        backgroundColor: 'rgba(255,255,255,0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '20px',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    },
    cardHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        marginBottom: '8px',
    },
    installerName: {
        fontSize: '17px',
        fontWeight: '700',
        color: '#1d1d1f',
    },
    cardDetail: {
        margin: '0 0 4px 0',
        fontSize: '14px',
        color: '#86868b',
    },
    skillsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        marginTop: '12px',
    },
    skillChip: {
        padding: '3px 10px',
        backgroundColor: 'rgba(0,122,255,0.08)',
        color: '#0071e3',
        borderRadius: '980px',
        fontSize: '12px',
        fontWeight: '600',
    },
    badgeGreen: {
        display: 'inline-block',
        padding: '3px 10px',
        backgroundColor: 'rgba(52,199,89,0.12)',
        color: '#248a3d',
        borderRadius: '980px',
        fontSize: '12px',
        fontWeight: '600',
        whiteSpace: 'nowrap',
    },
    badgeGray: {
        display: 'inline-block',
        padding: '3px 10px',
        backgroundColor: 'rgba(142,142,147,0.15)',
        color: '#86868b',
        borderRadius: '980px',
        fontSize: '12px',
        fontWeight: '600',
        whiteSpace: 'nowrap',
    },
    pagination: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        marginTop: '24px',
    },
    pageButton: {
        padding: '10px 20px',
        backgroundColor: 'rgba(255,255,255,0.7)',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#1d1d1f',
        cursor: 'pointer',
    },
    pageInfo: {
        fontSize: '14px',
        color: '#86868b',
        fontWeight: '500',
    },
};
//# sourceMappingURL=InstallerList.js.map